import React, { useEffect, useRef, useState } from "react";
import { MdGridView } from "react-icons/md";
import { IoClose, IoSettingsOutline } from "react-icons/io5";
import { LuVolume1, LuVolume2, LuVolumeOff } from "react-icons/lu";
import { useLiveData } from "../../../../hooks/useLiveData";
import { useRemote } from "../../../../hooks/useRemote";
import { useTV } from "../../../../hooks/useTV";
import useGlobalStore from "../../../../store/global.store";
import useVipMultiviewSoundStore from "../../../../store/useVipMultiviewSoundStore";
import YouTubePlayer from "../YouTubePlayer";
import SyncFullscreen from "../BeaconMultiViewDisplay/SyncFullscreen";
import useBeaconCore, {
  formatCountdown,
  prettyShow,
  extractYouTubeId,
} from "../useBeaconCore";
import {
  PRE_RECORDED_NAMES,
  TOP_STREAMERS,
  BLOCKED_VIDEO_IDS,
  getBackgroundByEST,
} from "../multi-view.utils";

// STEP 1 of the TV Beacon port: the useBeaconCore hook is now wired in,
// running all polling / state machines / BLE / dev helpers underneath the
// existing TV grid. NO Beacon UI is rendered yet — this step only proves
// the hook is UI-agnostic and doesn't disturb the existing TV experience.
// The `beacon` object is available for the next steps (badges, info strip,
// SyncFullscreen mount, remote-driven picker).

// Step 2b: full-width Beacon status bar.
// BEACON_BAR_VH is the bar height as a % of viewport height (the TV screen
// is ~16:9, so vh ≈ % of screen height). When the bar is shown, the grid
// scales DOWN by this factor in BOTH width and height so every video cell
// keeps its exact aspect ratio (no squashing). When the bar is hidden, the
// grid renders byte-identical to the current production layout.
const BEACON_BAR_VH = 9;
const LS_TV_BEACON_BAR = "mb_tv_status_bar";

// Tiny localStorage helpers (TV doesn't import the shared ls utils).
const tvLsGetBool = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    if (v == null) return fallback;
    return v === "1" || v === "true";
  } catch {
    return fallback;
  }
};
const tvLsSet = (key, val) => {
  try {
    localStorage.setItem(key, val ? "1" : "0");
  } catch {}
};

const BeaconMultiViewTV = () => {
  const { liveStreams, addEndedStream } = useLiveData();
  const { isMuted, toggleMute, increaseVolume, decreaseVolume, setMuted } =
    useVipMultiviewSoundStore();
  const { updateView } = useRemote();
  const { isTv } = useTV();

  const device = useGlobalStore((state) => state.device);
  const beaconDeviceId = device?.magic_beacon_device?._id ?? null;

  const [totalPlayers, setTotalPlayers] = useState(4);
  // Calculate grid dimensions based on total players
  const gridSize = totalPlayers <= 2 ? 2 : Math.ceil(Math.sqrt(totalPlayers));

  const setShowBottomBar = useGlobalStore((state) => state.setShowBottomBar);
  const [showGridConfigModal, setShowGridConfigModal] = useState(false);
  const [selectedGridOption, setSelectedGridOption] = useState(0);

  const [videoIds, setVideoIds] = useState(Array(totalPlayers).fill(""));
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currentVideoIds, setCurrentVideoIds] = useState(
    Array(totalPlayers).fill("")
  );
  const [recentlyEndedIds, setRecentlyEndedIds] = useState([]);
  const [fullscreenIndex, setFullscreenIndex] = useState(null);
  const [showControls, setShowControls] = useState(true);

  // Step 2b: whether the full-width Beacon status bar is shown. Persisted
  // so it survives TV reloads. When false, the grid is exactly the current
  // production layout.
  const [showBeaconBar, setShowBeaconBar] = useState(() =>
    tvLsGetBool(LS_TV_BEACON_BAR, true)
  );
  const toggleBeaconBar = () => {
    setShowBeaconBar((prev) => {
      const next = !prev;
      tvLsSet(LS_TV_BEACON_BAR, next);
      return next;
    });
  };

  const [backgroundImage, setBackgroundImage] = useState(getBackgroundByEST());
  const [endedIndexes, setEndedIndexes] = useState([]);
  const [allThumbnails, setAllThumbnails] = useState([]);
  const [showVideoThumbnails, setShowVideoThumbnails] = useState(false);
  const [selectedThumbnailIndex, setSelectedThumbnailIndex] = useState(0);
  // Track whether the focus change is coming from user interaction or programmatic
  const isUserFocus = useRef(true);

  const hasInteracted = useRef(false);
  const lastTapRef = useRef(0);
  // Bar-focus model: pressing Up from the top grid row moves focus to
  // the Beacon bar (when it's actionable). Select while the bar has
  // focus fires the CTA. Down from the bar returns to the grid. This
  // replaces the older "press Down twice" gesture — focus → Select is
  // the native TV navigation pattern and avoids the awkward double-beat.
  const [beaconBarFocused, setBeaconBarFocused] = useState(false);
  const beaconBarRef = useRef(null);

  // Remote-driven picker overlay nav. pickerIndex 0 = the CLOSE tile;
  // items start at 1. pickerRefs holds the tile button elements.
  const [pickerIndex, setPickerIndex] = useState(0);
  const pickerRefs = useRef([]);

  // Peer-handoff overlay (TV replacement for Display's gold tap-bubble).
  // peerOffer = { streamId, name } | null. peerIndex 0 = YES, 1 = NO.
  const [peerOffer, setPeerOffer] = useState(null);
  const [peerIndex, setPeerIndex] = useState(0);
  const peerRefs = useRef([]);

  // Refs for thumbnail focus management
  const thumbnailRefs = useRef([]);
  // Refs for video grid focus management
  const videoGridRefs = useRef([]);
  // Refs to each YouTubePlayer instance (for FORCE LIVE)
  const playerRefs = useRef([]);
  const forceLiveAtIndex = (index) => {
    playerRefs.current[index]?.jumpToLive?.();
  };

  // ===== Beacon brain (step 1: wired but no UI yet) =====
  // Runs all polling, mb-status/info-strip/fs-badge ticks, the cue runner,
  // BLE, picker logic, peer handoff, and the window.beacon* dev helpers.
  // We pass the TV's existing slot state in. Nothing here renders yet; the
  // next steps will read beacon.* to draw the TV Beacon UI.
  const beacon = useBeaconCore({
    liveStreams,
    videoIds,
    selectedIndex,
    fullscreenIndex,
    isTv,
    setVideoIds,
    setCurrentVideoIds,
    beaconDeviceId,
    isMuted,
    toggleMute,
    increaseVolume,
    decreaseVolume,
  });

  useEffect(() => {
    setShowBottomBar(false);
    setMuted(false);
    const interval = setInterval(() => {
      setBackgroundImage(getBackgroundByEST());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Grid config modal navigation has highest priority
      if (showGridConfigModal) {
        switch (e.key) {
          case "ArrowLeft":
            setSelectedGridOption((prev) => Math.max(0, prev - 1));
            break;
          case "ArrowRight":
            setSelectedGridOption((prev) => Math.min(4, prev + 1));
            break;
          case "Enter":
            if (selectedGridOption === 0) {
              setTotalPlayers(4); // 4 players (2x2)
              // Leaving 1-screen → clear fullscreenIndex so the hook's
              // fsBadges (gated on fullscreenIndex != null) is cleared
              // and the multi-grid doesn't show an in-slot AUTOSYNC badge.
              setFullscreenIndex(null);
              const previousCount = totalPlayers;
              const newIds = Array(4).fill("");

              // Preserve existing videos
              videoIds.forEach((id, i) => {
                if (i < 4) newIds[i] = id;
              });

              // Auto-populate new slots if available
              if (previousCount < 4) {
                const availableIds = getAvailableVideoIds(
                  videoIds.filter((id) => id)
                );
                for (let i = previousCount; i < 4; i++) {
                  if (!newIds[i] && availableIds.length > 0) {
                    newIds[i] = availableIds.shift();
                  }
                }
              }

              setVideoIds(newIds);
              setCurrentVideoIds(newIds);
            } else if (selectedGridOption === 1) {
              setTotalPlayers(3); // 3 players
              setFullscreenIndex(null);
              const previousCount = totalPlayers;
              const newIds = Array(3).fill("");

              // Preserve existing videos
              videoIds.forEach((id, i) => {
                if (i < 3) newIds[i] = id;
              });

              // Auto-populate new slots if available
              if (previousCount < 3) {
                const availableIds = getAvailableVideoIds(
                  videoIds.filter((id) => id)
                );
                for (let i = previousCount; i < 3; i++) {
                  if (!newIds[i] && availableIds.length > 0) {
                    newIds[i] = availableIds.shift();
                  }
                }
              }

              setVideoIds(newIds);
              setCurrentVideoIds(newIds);
            } else if (selectedGridOption === 2) {
              setTotalPlayers(2); // 2 players
              setFullscreenIndex(null);
              const previousCount = totalPlayers;
              const newIds = Array(2).fill("");

              // Preserve existing videos
              videoIds.forEach((id, i) => {
                if (i < 2) newIds[i] = id;
              });

              // Auto-populate new slots if available
              if (previousCount < 2) {
                const availableIds = getAvailableVideoIds(
                  videoIds.filter((id) => id)
                );
                for (let i = previousCount; i < 2; i++) {
                  if (!newIds[i] && availableIds.length > 0) {
                    newIds[i] = availableIds.shift();
                  }
                }
              }

              setVideoIds(newIds);
              setCurrentVideoIds(newIds);
            } else if (selectedGridOption === 3) {
              // 1-screen on TV is the equivalent of Display's double-tap
              // fullscreen. Apply the same locked-stream rule: if the
              // selected slot's streamer is LOCKED and we're not already
              // in sync, enter sync directly (doors → HLS) instead of
              // just shrinking the grid down to one fullscreen embed.
              {
                const selVid = videoIds[selectedIndex];
                const selStreamer = videoIdToStreamer[selVid];
                const selLocked =
                  beacon.getMagicStateForVideoId(selVid, selStreamer) ===
                  "proven";
                if (selLocked && !beacon.syncMode.active) {
                  beacon.enterSyncMode(selectedIndex);
                  console.log("Selected Grid Option: ", selectedGridOption);
                  setShowGridConfigModal(false);
                  setShowVideoThumbnails(false);
                  break;
                }
              }
              setTotalPlayers(1); // 1 player (fullscreen)
              const newIds = Array(1).fill("");

              // Use the currently selected video if it exists
              if (videoIds[selectedIndex]) {
                newIds[0] = videoIds[selectedIndex];
              } else {
                // Or get a new video if available
                const availableIds = getAvailableVideoIds([]);
                if (availableIds.length > 0) {
                  newIds[0] = availableIds[0];
                }
              }

              setVideoIds(newIds);
              setCurrentVideoIds(newIds);
              // Tell the hook this slot is "fullscreen" — the hook gates
              // its fsBadges compute on fullscreenIndex != null. Without
              // this, the in-slot AUTOSYNC badge and the countdown
              // auto-promote into doors/HLS never fire on TV's 1-screen
              // mode (parity with Display's fullscreen). Slot 0 is the
              // only slot now.
              setFullscreenIndex(0);
            } else if (selectedGridOption === 4) {
              // Close
            }
            console.log("Selected Grid Option: ", selectedGridOption);
            setShowGridConfigModal(false);
            setShowVideoThumbnails(false);
            break;
          case "Escape":
            setShowGridConfigModal(false);
            break;
          default:
            break;
        }
      } else if (showVideoThumbnails) {
        // Thumbnail navigation mode
        const maxIndex = allThumbnails.length + 4; // vip_exit, close, settings, beacon_toggle, force_live
        e.preventDefault();
        switch (e.key) {
          case "ArrowLeft":
            setSelectedThumbnailIndex((prev) => {
              const newIndex = Math.max(0, prev - 1);
              // Focus the corresponding thumbnail button
              if (thumbnailRefs.current[newIndex]) {
                thumbnailRefs.current[newIndex].focus();
                thumbnailRefs.current[newIndex].scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                  inline: "center",
                });
              }
              return newIndex;
            });
            break;
          case "ArrowRight":
            setSelectedThumbnailIndex((prev) => {
              const newIndex = Math.min(maxIndex, prev + 1);
              // Focus the corresponding thumbnail button
              if (thumbnailRefs.current[newIndex]) {
                thumbnailRefs.current[newIndex].focus();
                thumbnailRefs.current[newIndex].scrollIntoView({
                  behavior: "smooth",
                  block: "center",
                  inline: "center",
                });
              }
              return newIndex;
            });
            break;
          case "Enter":
            if (selectedThumbnailIndex === 0) {
              updateView("VIP", () => {
                setShowBottomBar(true);
              });
              break;
            }

            if (selectedThumbnailIndex === 1) {
              setShowVideoThumbnails(false);
              break;
            }
            if (selectedThumbnailIndex === 2) {
              console.log("settings");
              setShowVideoThumbnails(false);
              setShowGridConfigModal(true);
              setSelectedGridOption(0);
              break;
            }
            if (selectedThumbnailIndex === 3) {
              forceLiveAtIndex(selectedIndex);
              setShowVideoThumbnails(false);
              break;
            }
            if (selectedThumbnailIndex === 4) {
              toggleBeaconBar();
              break;
            
            }
            if (
              allThumbnails[selectedThumbnailIndex - 5] &&
              !currentVideoIds.includes(
                allThumbnails[selectedThumbnailIndex - 5].videoId
              )
            ) {
              handleVideoSelect(
                allThumbnails[selectedThumbnailIndex - 5].videoId
              );
              setShowVideoThumbnails(false);
            }
            break;
          case "Escape":
            setShowVideoThumbnails(false);
            break;
          default:
            break;
        }
      } else {
        // Video grid navigation mode
        switch (e.key) {
          case "ArrowLeft":
            setSelectedIndex((prev) => {
              const newIndex = prev % gridSize !== 0 ? prev - 1 : prev;
              if (videoGridRefs.current[newIndex]) {
                videoGridRefs.current[newIndex].focus();
              }
              return newIndex;
            });
            break;
          case "ArrowRight":
            setSelectedIndex((prev) => {
              const newIndex =
                (prev + 1) % gridSize !== 0 && prev < totalPlayers - 1
                  ? prev + 1
                  : prev;
              if (videoGridRefs.current[newIndex]) {
                videoGridRefs.current[newIndex].focus();
              }
              return newIndex;
            });
            break;
          case "ArrowUp":
            // Top row → focus the Beacon bar if it has an action.
            // Otherwise: standard "up one row" navigation.
            if (
              selectedIndex < gridSize &&
              showBeaconBar &&
              beaconBarHasAction() &&
              beaconBarRef.current
            ) {
              beaconBarRef.current.focus();
              setBeaconBarFocused(true);
              break;
            }
            setSelectedIndex((prev) => {
              const newIndex = prev >= gridSize ? prev - gridSize : prev;
              if (videoGridRefs.current[newIndex]) {
                videoGridRefs.current[newIndex].focus();
              }
              return newIndex;
            });
            break;
          case "ArrowDown":
            setSelectedIndex((prev) => {
              // Special case for 3-player layout when on top-right video (index 1)
              if (totalPlayers === 3 && prev === 1) {
                const newIndex = 2; // Move to the bottom video (index 2)
                if (videoGridRefs.current[newIndex]) {
                  videoGridRefs.current[newIndex].focus();
                }
                return newIndex;
              }

              // Normal grid navigation
              const newIndex =
                prev < totalPlayers - gridSize ? prev + gridSize : prev;
              if (videoGridRefs.current[newIndex]) {
                videoGridRefs.current[newIndex].focus();
              }
              return newIndex;
            });
            break;
          case "Enter":
            setShowVideoThumbnails(true);
            setSelectedThumbnailIndex(0);
            break;
          default:
            break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    showVideoThumbnails,
    selectedThumbnailIndex,
    allThumbnails,
    currentVideoIds,
    showGridConfigModal,
    selectedGridOption,
    totalPlayers,
    gridSize,
    videoIds,
    showBeaconBar,
    selectedIndex,
    beacon.mbStatus,
    beacon.infoStrip,
  ]);

  // ===== Overlay remote navigation (picker + peer-handoff) =====
  // Highest priority: when either overlay is open it captures all remote
  // input (arrows move focus, Enter selects, Escape/Back closes/declines),
  // stopping the event from reaching the grid/thumbnail handler.
  useEffect(() => {
    const pickerOpen = beacon.streamPicker.open;
    const peerOpen = !!peerOffer;
    if (!pickerOpen && !peerOpen) return;

    const onKey = (e) => {
      // Peer overlay takes precedence (it can appear over a sync wait).
      if (peerOpen) {
        e.preventDefault();
        e.stopPropagation();
        switch (e.key) {
          case "ArrowLeft":
            setPeerIndex(0);
            peerRefs.current[0]?.focus();
            break;
          case "ArrowRight":
            setPeerIndex(1);
            peerRefs.current[1]?.focus();
            break;
          case "Enter":
            if (peerIndex === 0) {
              beacon.peerHandoff(peerOffer.streamId);
            }
            setPeerOffer(null);
            break;
          case "Escape":
            setPeerOffer(null);
            break;
          default:
            break;
        }
        return;
      }

      // Picker overlay: index 0 = CLOSE tile, items 1..N.
      const total = beacon.streamPicker.items.length + 1;
      e.preventDefault();
      e.stopPropagation();
      switch (e.key) {
        case "ArrowLeft":
          setPickerIndex((prev) => {
            const n = Math.max(0, prev - 1);
            pickerRefs.current[n]?.focus();
            return n;
          });
          break;
        case "ArrowRight":
          setPickerIndex((prev) => {
            const n = Math.min(total - 1, prev + 1);
            pickerRefs.current[n]?.focus();
            return n;
          });
          break;
        case "ArrowDown":
case "ArrowUp": {
  // Spatial nav: jump to the geometrically nearest tile in the
  // next/previous rendered row (handles CLOSE tile sizing + ragged rows).
  const rects = pickerRefs.current.map((r) =>
    r ? { top: r.offsetTop, left: r.offsetLeft } : null
  );
  const cur = rects[pickerIndex];
  if (!cur) break;
  const down = e.key === "ArrowDown";
  // candidate rows strictly below (or above) the current tile
  const candTops = rects
    .filter((r) => r && (down ? r.top > cur.top : r.top < cur.top))
    .map((r) => r.top);
  if (candTops.length === 0) break; // already on edge row — do nothing
  const targetTop = down ? Math.min(...candTops) : Math.max(...candTops);
  // nearest horizontally within that row
  let best = -1, bestDist = Infinity;
  rects.forEach((r, i) => {
    if (!r || r.top !== targetTop) return;
    const d = Math.abs(r.left - cur.left);
    if (d < bestDist) { bestDist = d; best = i; }
  });
  if (best >= 0) {
    setPickerIndex(best);
    pickerRefs.current[best]?.focus();
  }
  break;
}
        case "Enter":
          if (pickerIndex === 0) {
            beacon.closeStreamPicker();
          } else {
            const item = beacon.streamPicker.items[pickerIndex - 1];
            if (item) beacon.handlePickerTileClick(item);
          }
          break;
        case "Escape":
          beacon.closeStreamPicker();
          break;
        default:
          break;
      }
    };

    // Capture phase so we intercept before the grid/thumbnail handler.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [
    beacon.streamPicker.open,
    beacon.streamPicker.items,
    pickerIndex,
    peerOffer,
    peerIndex,
  ]);

  // When the picker OPENS (closed -> open transition only), reset focus to
  // the CLOSE tile. Lock updates that refresh the picker while it's already
  // open must NOT re-reset — that was yanking focus mid-navigation.
  const pickerWasOpenRef = useRef(false);
  useEffect(() => {
    const open = beacon.streamPicker.open;
    const justOpened = open && !pickerWasOpenRef.current;
    pickerWasOpenRef.current = open;
    if (justOpened) {
      setPickerIndex(0);
      const t = setTimeout(() => pickerRefs.current[0]?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [beacon.streamPicker.open]);

  // While the picker stays open and its ITEMS churn (locks arriving), the
  // focused tile can remount and silently drop DOM focus. Re-align focus to
  // the current pickerIndex — but never fight the user: skip if focus is
  // already on one of our tiles.
  useEffect(() => {
    if (!beacon.streamPicker.open) return;
    const t = setTimeout(() => {
      const a = document.activeElement;
      if (pickerRefs.current.includes(a)) return;
      const max = beacon.streamPicker.items.length; // CLOSE is 0, items 1..N
      const n = Math.min(pickerIndex, max);
      pickerRefs.current[n]?.focus();
    }, 80);
    return () => clearTimeout(t);
  }, [beacon.streamPicker.items, beacon.streamPicker.open, pickerIndex]);

  // When the peer overlay opens, default focus to YES (index 0).
  useEffect(() => {
    if (peerOffer) {
      setPeerIndex(0);
      const t = setTimeout(() => peerRefs.current[0]?.focus(), 60);
      return () => clearTimeout(t);
    }
  }, [peerOffer]);

  // ===== Peer-handoff detection (TV) =====
  // While in a sync session that is still WAITING (early-entry, no anchor
  // locked yet), poll for another streamer already locked on the same
  // show. If one appears, pop the YES/NO overlay. Mirrors Display's gold
  // peer bubble, using the same beacon.findLockedPeer. Clears the offer
  // once an anchor arrives (we locked), sync exits, or no peer remains.
  useEffect(() => {
    const sm = beacon.syncMode;
    // Only offer during an early-entry wait (no own anchor yet).
    const waiting = sm.active && sm.earlyEntry && !sm.anchor;
    if (!waiting) {
      if (peerOffer) setPeerOffer(null);
      return;
    }
    const showId = sm.earlyEntryShowId;
    if (!showId) return;
    const check = () => {
      const peer = beacon.findLockedPeer(showId, sm.streamId);
      setPeerOffer((prev) => {
        if (peer && (!prev || prev.streamId !== peer.streamId)) return peer;
        if (!peer && prev) return null;
        return prev;
      });
    };
    check();
    const id = setInterval(check, 1500);
    return () => clearInterval(id);
  }, [
    beacon.syncMode.active,
    beacon.syncMode.earlyEntry,
    beacon.syncMode.anchor,
    beacon.syncMode.earlyEntryShowId,
    beacon.syncMode.streamId,
  ]);

  // ===== Universal sync-exit → 4-grid restoration =====
  // The hook deactivates syncMode for multiple reasons:
  //   - User pressed EXIT (calls beacon.exitSyncMode("user closed"))
  //   - Clean show-end (line 2478 of useBeaconCore.js → handleAnchorUpdate(null))
  //   - Mid-show unlock past grace window
  //   - Beacon disabled by user
  // Only the EXIT-button path previously restored to 4-grid via the
  // SyncFullscreen onExit callback. Show-end & friends bypassed onExit
  // entirely, leaving the TV in whatever fullscreenIndex / totalPlayers
  // state it had pre-sync (typically 1-screen if auto-promoted from
  // fullscreen embed). This effect catches the true → false transition
  // from ANY path and runs the 4-grid restoration, replacing the
  // duplicated logic in onExit.
  const prevSyncActiveRef = useRef(false);
  useEffect(() => {
    const wasActive = prevSyncActiveRef.current;
    const isActive = beacon.syncMode.active;
    prevSyncActiveRef.current = isActive;
    if (!wasActive || isActive) return;
    // Sync just deactivated — restore 4-grid.
    setFullscreenIndex(null);
    const newIds = Array(4).fill("");
    videoIds.forEach((id, i) => {
      if (i < 4) newIds[i] = id;
    });
    if (totalPlayers < 4) {
      const availableIds = getAvailableVideoIds(
        videoIds.filter((id) => id)
      );
      for (let i = 0; i < 4; i++) {
        if (!newIds[i] && availableIds.length > 0) {
          newIds[i] = availableIds.shift();
        }
      }
    }
    setTotalPlayers(4);
    setVideoIds(newIds);
    setCurrentVideoIds(newIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [beacon.syncMode.active]);

  // Focus management for thumbnail mode. Skipped when any overlay
  // (picker, peer, grid-config modal) is open, or sync is active —
  // those have their own focus management and the 150ms refocus
  // would yank focus back to a thumbnail. Proper cleanup: the
  // timeoutId is captured in outer scope so re-running the effect
  // (or unmounting) cancels any pending focus call.
  useEffect(() => {
    const overlayOpen =
      beacon.streamPicker.open ||
      !!peerOffer ||
      showGridConfigModal ||
      beacon.syncMode.active;
    const shouldFocus =
      showVideoThumbnails &&
      !overlayOpen &&
      thumbnailRefs.current[selectedThumbnailIndex];

    if (shouldFocus) {
      isUserFocus.current = false;
      let timeoutId = null;
      const animFrameId = requestAnimationFrame(() => {
        timeoutId = setTimeout(() => {
          if (
            document.activeElement !==
            thumbnailRefs.current[selectedThumbnailIndex]
          ) {
            thumbnailRefs.current[selectedThumbnailIndex]?.focus();
          }
          isUserFocus.current = true;
        }, 150);
      });
      return () => {
        cancelAnimationFrame(animFrameId);
        if (timeoutId) clearTimeout(timeoutId);
        isUserFocus.current = true;
      };
    }
  }, [
    showVideoThumbnails,
    selectedThumbnailIndex,
    beacon.streamPicker.open,
    peerOffer,
    showGridConfigModal,
    beacon.syncMode.active,
  ]);

  // Focus management for video grid mode. Skipped when the Beacon bar
  // has focus, any overlay (picker, peer) is open, or sync is active
  // — those have their own focus management and the 100ms refocus
  // would otherwise race their explicit focus() calls and snap focus
  // back to the slot. Uses proper cleanup: re-running the effect
  // cancels any in-flight setTimeout from the previous run, so a fast
  // ArrowUp → bar.focus() can't be undone by a stale grid timer.
  useEffect(() => {
    const overlayOpen = beacon.streamPicker.open || !!peerOffer;
    if (
      !showVideoThumbnails &&
      !showGridConfigModal &&
      !beaconBarFocused &&
      !overlayOpen &&
      !beacon.syncMode.active &&
      videoGridRefs.current[selectedIndex]
    ) {
      const t = setTimeout(() => {
        videoGridRefs.current[selectedIndex]?.focus();
      }, 100);
      return () => clearTimeout(t);
    }
  }, [
    showVideoThumbnails,
    showGridConfigModal,
    selectedIndex,
    beaconBarFocused,
    beacon.streamPicker.open,
    peerOffer,
    beacon.syncMode.active,
  ]);

  const toggleControls = () => {
    setShowControls((prev) => {
      localStorage.setItem("controlsVisible", !prev);
      return !prev;
    });
  };

  // videoIdToStreamer also derived by the hook; read it from there so the
  // TV grid labels stay in sync with what Beacon resolves.
  const videoIdToStreamer = beacon.videoIdToStreamer;

  const getSortedThumbnails = (streams) => {
    if (!streams) return [];

    return Object.entries(streams)
      .filter(
        ([name, data]) => data?.videoId && !BLOCKED_VIDEO_IDS.has(data.videoId)
      )
      .map(([name, data]) => ({
        streamer: data.name,
        videoId: data.videoId,
        title: data.title,
        thumbnail: data.thumbnail,
        isTop: TOP_STREAMERS.has(name),
        isLoop: PRE_RECORDED_NAMES.has(name),
      }))
      .sort((a, b) => {
        if (a.isTop !== b.isTop) return b.isTop - a.isTop;
        if (a.isLoop !== b.isLoop) return a.isLoop - b.isLoop;
        return 0;
      });
  };

  const getAvailableVideoIds = (currentPlaying = []) => {
    if (!liveStreams || Object.keys(liveStreams).length === 0) return [];

    const sorted = getSortedThumbnails(liveStreams);
    return sorted
      .map((v) => v.videoId)
      .filter(
        (id) =>
          !currentPlaying.includes(id) &&
          !recentlyEndedIds.includes(id) &&
          !BLOCKED_VIDEO_IDS.has(id)
      );
  };

  useEffect(() => {
    if (liveStreams && Object.keys(liveStreams).length > 0) {
      const availableIds = getAvailableVideoIds([]);

      // Make sure we don't duplicate videos across slots
      const newVideoIds = [...videoIds];
      const currentlyPlaying = new Set(newVideoIds.filter((id) => id));

      for (let i = 0; i < totalPlayers; i++) {
        if (!newVideoIds[i] && availableIds.length > 0) {
          // Find the first available ID that's not already playing
          const nextAvailableId = availableIds.find(
            (id) => !currentlyPlaying.has(id)
          );

          if (nextAvailableId) {
            newVideoIds[i] = nextAvailableId;
            currentlyPlaying.add(nextAvailableId);
          }
        }
      }

      if (JSON.stringify(newVideoIds) !== JSON.stringify(videoIds)) {
        setVideoIds(newVideoIds);
        setCurrentVideoIds(newVideoIds);
      }
    }
  }, [liveStreams]);

  useEffect(() => {
    if (liveStreams) {
      const currentStreamIds = new Set(
        Object.values(liveStreams)
          .filter((data) => data?.videoId)
          .map((data) => data.videoId)
      );

      setAllThumbnails(getSortedThumbnails(liveStreams));

      const updatedVideoIds = [...videoIds];
      let needsUpdate = false;

      for (let i = 0; i < updatedVideoIds.length; i++) {
        const id = updatedVideoIds[i];
        if (id && !currentStreamIds.has(id)) {
          updatedVideoIds[i] = "";
          needsUpdate = true;
        }
      }

      if (needsUpdate) {
        setVideoIds(updatedVideoIds);
        setCurrentVideoIds(updatedVideoIds);

        const availableIds = getAvailableVideoIds(
          updatedVideoIds.filter((id) => id)
        );
        const finalVideoIds = [...updatedVideoIds];

        for (let i = 0; i < finalVideoIds.length; i++) {
          if (!finalVideoIds[i] && availableIds.length > 0) {
            finalVideoIds[i] = availableIds.shift();
          }
        }

        if (JSON.stringify(finalVideoIds) !== JSON.stringify(updatedVideoIds)) {
          setVideoIds(finalVideoIds);
          setCurrentVideoIds(finalVideoIds);
        }
      }
    }
  }, [liveStreams]);

  const handleSelectIndex = (index) => {
    setSelectedIndex(index);
    hasInteracted.current = true;
    setEndedIndexes((prev) => prev.filter((i) => i !== index));
  };

  const handleDoubleTap = (index) => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 400;
    if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      // Mirrors Display's handleDoubleTap (BeaconMultiViewDisplay.jsx
      // line 229): if the tapped slot's stream is LOCKED and we're not
      // already in sync, enter sync directly (doors → HLS) instead of
      // toggling fullscreen. The user clearly wants to watch the synced
      // experience. Otherwise, normal fullscreen toggle.
      const videoId = videoIds[index];
      const streamerName = videoIdToStreamer[videoId];
      const isLocked =
        beacon.getMagicStateForVideoId(videoId, streamerName) === "proven";
      if (isLocked && !beacon.syncMode.active) {
        lastTapRef.current = 0;
        beacon.enterSyncMode(index);
        return;
      }
      setFullscreenIndex((prev) => (prev === index ? null : index));
    } else {
      console.log("Single Tap");
      setShowVideoThumbnails(true);
      lastTapRef.current = now;
    }
  };

  const handleVideoSelect = (newVideoId) => {
    if (currentVideoIds.includes(newVideoId)) return;

    const updated = [...videoIds];
    updated[selectedIndex] = newVideoId;
    setVideoIds(updated);
    setCurrentVideoIds(updated);
    setEndedIndexes((prev) => prev.filter((i) => i !== selectedIndex));
  };

  const replaceVideoAtIndex = async (index, oldVideoId) => {
    try {
      // Add the ended video ID to the recently ended list
      const newRecentlyEndedIds = [
        ...new Set([...recentlyEndedIds, oldVideoId]),
      ];

      // Create a copy of the current video IDs and mark this slot as empty
      const currentPlaying = [...videoIds];
      currentPlaying[index] = "";

      // Get IDs that are already playing in other slots
      const currentlyPlayingIds = new Set(
        currentPlaying.filter((id, idx) => id && idx !== index)
      );

      // Get available videos excluding currently playing ones
      const availableIds = getAvailableVideoIds(
        currentPlaying.filter((id) => id)
      );

      // Find an available ID that's not already playing in another slot
      const uniqueAvailableId = availableIds.find(
        (id) => !currentlyPlayingIds.has(id)
      );

      // Prepare the final state update
      const updatedVideoIds = [...currentPlaying];
      let updatedEndedIndexes = [...endedIndexes.filter((i) => i !== index)];

      if (uniqueAvailableId) {
        updatedVideoIds[index] = uniqueAvailableId;
      } else {
        updatedEndedIndexes = [...updatedEndedIndexes, index];
      }

      // Update all state in a single render cycle
      setRecentlyEndedIds(newRecentlyEndedIds);
      setVideoIds(updatedVideoIds);
      setCurrentVideoIds(updatedVideoIds);
      setEndedIndexes(updatedEndedIndexes);
    } catch (err) {
      console.error("Video replace error:", err);
    }
  };

  useEffect(() => {
    if (liveStreams) {
      setAllThumbnails(getSortedThumbnails(liveStreams));
    } else {
      setAllThumbnails([]);
    }
  }, [liveStreams]);

  const handleOnVideoEnded = (videoId) => {
    console.log("onVideoEnded", videoId);
    // Add the ended video ID to the ended streams list
    addEndedStream(videoId);

    // Find the index of the video that ended
    // const index = videoIds.findIndex((id) => id === videoId);
    // if (index !== -1) {
    //   replaceVideoAtIndex(index, videoId);
    // }
  };

  // Handle thumbnail focus to prevent focus loops
  const handleThumbnailFocus = (index) => {
    // Skip if we're already in the process of programmatic focus
    if (!isUserFocus.current) return;

    // Don't update if the index is already selected (prevents loops)
    if (index === selectedThumbnailIndex) return;

    // Update the selected index
    setSelectedThumbnailIndex(index);
  };

  // When showing thumbnails, reset the selected thumbnail index
  useEffect(() => {
    if (showVideoThumbnails) {
      // Only reset if we're showing thumbnails for the first time
      setSelectedThumbnailIndex(0);
    }
  }, [showVideoThumbnails]);

  // Update layout when totalPlayers changes
  useEffect(() => {
    // Ensure selected index is valid for the new layout
    if (selectedIndex >= totalPlayers) {
      setSelectedIndex(totalPlayers - 1);
    }
  }, [totalPlayers, selectedIndex]);

  // ===== Step 2b: full-width Beacon status bar renderer =====
  // The TV bar is the "big brother" of Display's two Beacon elements: the
  // purple/green top-right badge (beacon.mbStatus) AND the show-time pill
  // below the embeds (beacon.infoStrip). It speaks the EXACT same wording
  // so a customer with both a TV and a Display device sees them as obviously
  // the same feature.
  //
  // Priority: mbStatus rows (locked/syncing/countdown/upcoming/standby/
  // closed) drive the bar when present — that's the richer, more actionable
  // data. When mbStatus is empty, fall back to the infoStrip content
  // (Now Playing / That's a wrap / Up Next / Starting Now) so the bar still
  // says what the pill would. Wording is copied verbatim from Display; the
  // "TAP HERE" phrasing stays for now (Eric will swap to "Press OK" later).
  //
  // Color language (matches Display): green pulse for actionable CTA
  // (locked or countdown-cta), purple for the "wrap / ended" wind-down, and
  // the neutral gold for passive upcoming/standby states.
  const renderBeaconBar = () => {
    const { lockedRows = [], nonLockedRows = [] } = beacon.mbStatus || {};
    const strip = beacon.infoStrip || {};
    const hasMbStatus = lockedRows.length > 0 || nonLockedRows.length > 0;
    const isWrap =
      !hasMbStatus && strip.visible && strip.prefix === "That's a wrap!";

    // Bar CTA wording adapts to focus state:
    //   not focused  → "PRESS UP THEN OK TO SYNC NOW!" (teaches the gesture)
    //   focused      → "PRESS OK TO SYNC NOW!"        (you're here, hit it)
    const ctaText = beaconBarFocused
      ? "PRESS OK TO SYNC NOW!"
      : "PRESS UP THEN OK TO SYNC NOW!";

    // ---- Palette: PURPLE + GREEN only, copied from Display's mb-status
    // (SyncFullscreen.css). No gold/cyan accents — the bar should read as
    // the same feature as the Display badge. ----
    const FONT = "Lato, -apple-system, BlinkMacSystemFont, system-ui, sans-serif";
    const CREAM = "#fde68a";   // label + show/time on purple states
    const WHITE = "#ffffff";
    const LILAC = "#e9d5ff";   // "@" and pending time
    const PRE_WHITE = "#ffffff";
    const G_LABEL = "#86efac"; // locked label
    const G_DETAIL = "#bbf7d0"; // locked detail
    const PEND_SHOW = "#d8b4fe";
    const PEND_DETAIL = "#c4b5fd";
    const PEND_TIME = "#e9d5ff";

    const BAR_PURPLE_BG =
      "linear-gradient(160deg, rgba(8,8,24,0.92) 0%, rgba(30,12,54,0.90) 50%, rgba(8,8,24,0.92) 100%)";
    const BAR_GREEN_BG =
      "linear-gradient(160deg, rgba(6,30,18,0.93) 0%, rgba(10,46,26,0.90) 50%, rgba(6,30,18,0.93) 100%)";

    // Shared white glare-sweep element used by every state's bar.
    const glare = (
      <div
        style={{
          position: "absolute",
          top: "-50%",
          left: "-120%",
          width: "55%",
          height: "200%",
          background:
            "linear-gradient(105deg, transparent 0%, rgba(255,255,255,0) 30%, rgba(255,255,255,0.22) 48%, rgba(255,255,255,0.42) 50%, rgba(255,255,255,0.22) 52%, rgba(255,255,255,0) 70%, transparent 100%)",
          transform: "skewX(-20deg)",
          filter: "blur(2px)",
          mixBlendMode: "screen",
          pointerEvents: "none",
          zIndex: 3,
          animation: "mbBarGlare 5s ease-in-out infinite",
        }}
      />
    );
    // Gold-shimmer CLICK HERE! — centered call-to-action for the two
    // clickable bar states (pre-show window + locked). Absolutely
    // centered so it never disturbs the existing left/right layout.
    const clickHere = (
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%,-50%)",
          zIndex: 2,
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}
      >
        <span
          className="mb-click-here"
          style={{
            fontFamily: FONT,
            fontSize: "2.0vw",
            fontWeight: 900,
            letterSpacing: "0.12em",
          }}
        >
          CLICK HERE!
        </span>
        <style>{`
          .mb-click-here {
            background: linear-gradient(100deg, #b8860b 0%, #ffd700 25%, #fff6c9 50%, #ffd700 75%, #b8860b 100%);
            background-size: 200% auto;
            -webkit-background-clip: text;
            background-clip: text;
            color: transparent;
            animation: mbGoldShimmer 2.2s linear infinite;
            filter: drop-shadow(0 0 10px rgba(255,215,0,0.35));
          }
          @keyframes mbGoldShimmer {
            0% { background-position: 200% center; }
            100% { background-position: 0% center; }
          }
        `}</style>
      </div>
    );

    // Outer bar shell. `green` toggles the green locked palette + glow pulse.
    // The shell is focusable (tabIndex 0) whenever the bar has an action —
    // that's how the user enters sync on TV: ArrowUp from the top grid row
    // moves focus here, Select fires the action, Down returns to the grid.
    // When focused, the shell intensifies its existing color identity
    // (purple glows more purple, green glows more green) and scales up
    // slightly so it reads as "you're in the right spot, press OK."
    const hasAction = beaconBarHasAction();
    const barShell = (children, { green = false } = {}) => {
      // Focused state needs to be UNMISTAKABLE — the user has just
      // navigated up to a full-width banner, they should know it. So:
      //   • Brighter, thicker ring of the bar's own color across all 4 sides
      //   • A visible scale-up (not a subtle one)
      //   • An inner white outline so the edge "lights up"
      //   • The whole bar lifts with a strong drop shadow
      // No new colors — green bars glow brighter green, purple brighter purple.
      const focusGlow = green
        ? "0 0 0 5px #4ade80, 0 0 0 10px rgba(74,222,128,0.35), 0 14px 0 -2px rgba(255,255,255,0.85), 0 22px 40px -4px rgba(255,255,255,0.55), 0 18px 50px rgba(74,222,128,0.7), inset 0 0 0 3px rgba(255,255,255,0.55)"
        : "0 0 0 5px #c084fc, 0 0 0 10px rgba(192,132,252,0.35), 0 14px 0 -2px rgba(255,255,255,0.85), 0 22px 40px -4px rgba(255,255,255,0.55), 0 18px 50px rgba(192,132,252,0.7), inset 0 0 0 3px rgba(255,255,255,0.5)";
      const restGlow = green
        ? "0 6px 30px rgba(0,0,0,0.55), 0 0 34px rgba(74,222,128,0.55)"
        : "0 6px 28px rgba(0,0,0,0.55), 0 0 28px rgba(192,132,252,0.45)";
      return (
        <div
          ref={hasAction ? beaconBarRef : null}
          tabIndex={hasAction ? 0 : -1}
          role={hasAction ? "button" : undefined}
          onFocus={() => setBeaconBarFocused(true)}
          onBlur={() => setBeaconBarFocused(false)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              e.stopPropagation();
              fireBeaconBarAction();
              return;
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              e.stopPropagation();
              setBeaconBarFocused(false);
              const target = videoGridRefs.current[selectedIndex];
              if (target) target.focus();
              return;
            }
            if (
              e.key === "ArrowUp" ||
              e.key === "ArrowLeft" ||
              e.key === "ArrowRight"
            ) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          style={{
            flex: "0 0 auto",
            width: "100%",
            height: `${BEACON_BAR_VH}vh`,
            position: "relative",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 2.4vw",
            boxSizing: "border-box",
            fontFamily: FONT,
            background: green ? BAR_GREEN_BG : BAR_PURPLE_BG,
            borderBottom: green ? "3px solid #4ade80" : "3px solid #c084fc",
            boxShadow: beaconBarFocused && hasAction ? focusGlow : restGlow,
            transform:
              beaconBarFocused && hasAction
                ? "scale(1.03) translateY(2px)"
                : "scale(1) translateY(0)",
            transformOrigin: "top center",
            transition:
              "transform 240ms cubic-bezier(.2,.8,.2,1), box-shadow 240ms cubic-bezier(.2,.8,.2,1)",
            cursor: hasAction ? "pointer" : "default",
            outline: "none",
            animation:
              green && !(beaconBarFocused && hasAction)
                ? "mbBarPulse 1.9s ease-in-out infinite"
                : "none",
          }}
        >
          {children}
        </div>
      );
    };

    // Shared LEFT brand label ("SYNC YOUR BAND:").
    const brandLabel = (color) => (
      <div
        style={{
          fontFamily: FONT,
          fontSize: "1.5vw",
          fontWeight: 900,
          letterSpacing: "2.5px",
          lineHeight: 1.05,
          color,
          textShadow: "0 1px 3px rgba(0,0,0,0.55)",
          whiteSpace: "nowrap",
        }}
      >
        SYNC YOUR BAND:
      </div>
    );

    // ===== LOCKED / CONNECTED (green) =====
    if (lockedRows.length > 0) {
      const syncingRow = nonLockedRows.find((r) => r.type === "syncing");
      return barShell(
        <>
          {glare}
          {clickHere}
          {/* LEFT — connected action */}
          <div
            style={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            {brandLabel(G_LABEL)}
            <div
              style={{
                fontFamily: FONT,
                fontSize: "1.5vw",
                fontWeight: 800,
                lineHeight: 1.1,
                color: G_DETAIL,
                marginTop: "0.4vh",
                whiteSpace: "nowrap",
                animation: "mbTextPulse 1.5s ease-in-out infinite",
              }}
            >
              Connected! {ctaText}
            </div>
          </div>
          {/* RIGHT — Now Playing pill (+ side-by-side syncing block) */}
          <div
            style={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              gap: "2vw",
              whiteSpace: "nowrap",
            }}
          >
            {syncingRow && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  paddingRight: "2vw",
                  borderRight: "1px solid rgba(255,255,255,0.2)",
                }}
              >
                <div
                  style={{
                    fontFamily: FONT,
                    fontSize: "1.15vw",
                    fontWeight: 800,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: PEND_SHOW,
                  }}
                >
                  Also Syncing
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: "0.7vw",
                    marginTop: "0.2vh",
                  }}
                >
                  <span
                    style={{
                      fontFamily: FONT,
                      fontSize: "1.6vw",
                      fontWeight: 800,
                      color: PEND_SHOW,
                    }}
                  >
                    {syncingRow.names}
                  </span>
                  <span
                    style={{
                      fontFamily: FONT,
                      fontSize: "1.6vw",
                      fontWeight: 800,
                      color: PEND_TIME,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatCountdown(syncingRow.remaining)}
                  </span>
                </div>
              </div>
            )}
            {strip.visible && strip.show && (
              <div
                style={{ display: "flex", alignItems: "baseline", gap: "0.8vw" }}
              >
                <span
                  style={{
                    fontFamily: FONT,
                    fontSize: "1.35vw",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: G_DETAIL,
                  }}
                >
                  {strip.prefix}
                </span>
                <span
                  style={{
                    fontFamily: FONT,
                    fontSize: "1.9vw",
                    fontWeight: 800,
                    color: WHITE,
                    textShadow: "0 0 10px rgba(74,222,128,0.4)",
                  }}
                >
                  {strip.show}
                </span>
              </div>
            )}
          </div>
        </>,
        { green: true }
      );
    }

    // ===== SYNCING (purple) — big "Syncing to show" + big countdown center =====
    const syncingRow = nonLockedRows.find((r) => r.type === "syncing");
    if (syncingRow) {
      return barShell(
        <>
          {glare}
          {/* LEFT — brand label */}
          <div
            style={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              flex: "1 1 0",
              minWidth: 0,
            }}
          >
            {brandLabel(CREAM)}
          </div>
          {/* CENTER — big status + dominant countdown */}
          <div
            style={{
              position: "relative",
              zIndex: 2,
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "1.4vw",
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                fontFamily: FONT,
                fontSize: "2.1vw",
                fontWeight: 800,
                color: CREAM,
                animation: "mbTextPulse 1.6s ease-in-out infinite",
              }}
            >
              {syncingRow.detail}
            </span>
            <span
              style={{
                fontFamily: FONT,
                fontSize: "3.4vw",
                fontWeight: 900,
                lineHeight: 1,
                color: WHITE,
                fontVariantNumeric: "tabular-nums",
                textShadow: "0 0 20px rgba(192,132,252,0.55)",
              }}
            >
              {formatCountdown(syncingRow.remaining)}
            </span>
            <span
              style={{
                fontFamily: FONT,
                fontSize: "1.4vw",
                fontWeight: 800,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: LILAC,
                animation: "mbTextPulse 1.6s ease-in-out infinite",
              }}
            >
              PLEASE WAIT
            </span>
          </div>
          {/* RIGHT — Starting Now pill */}
          <div
            style={{
              position: "relative",
              zIndex: 2,
              flex: "1 1 0",
              minWidth: 0,
              display: "flex",
              alignItems: "baseline",
              justifyContent: "flex-end",
              gap: "0.8vw",
              whiteSpace: "nowrap",
            }}
          >
            {strip.visible && strip.show && (
              <>
                <span
                  style={{
                    fontFamily: FONT,
                    fontSize: "1.35vw",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: LILAC,
                  }}
                >
                  {strip.prefix}
                </span>
                <span
                  style={{
                    fontFamily: FONT,
                    fontSize: "1.9vw",
                    fontWeight: 800,
                    color: WHITE,
                  }}
                >
                  {strip.show}
                </span>
              </>
            )}
          </div>
        </>
      );
    }

    // ===== WITHIN 5 MIN (purple) — info-strip countdown is the signal =====
    if (strip.visible && strip.countdownStr) {
      return barShell(
        <>
          {glare}
          {clickHere}
          {/* LEFT — brand + action */}
          <div
            style={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            {brandLabel(CREAM)}
            <div
              style={{
                fontFamily: FONT,
                fontSize: "1.85vw",
                fontWeight: 900,
                lineHeight: 1.1,
                color: WHITE,
                marginTop: "0.4vh",
                whiteSpace: "nowrap",
                animation: "mbTextPulse 1.5s ease-in-out infinite",
              }}
            >
              {ctaText}
            </div>
          </div>
          {/* RIGHT — Up Next + live countdown */}
          <div
            style={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              alignItems: "baseline",
              gap: "0.8vw",
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                fontFamily: FONT,
                fontSize: "1.35vw",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: LILAC,
              }}
            >
              {strip.prefix}
            </span>
            <span
              style={{
                fontFamily: FONT,
                fontSize: "1.9vw",
                fontWeight: 800,
                color: WHITE,
              }}
            >
              {strip.show}
            </span>
            <span style={{ fontFamily: FONT, fontSize: "1.5vw", color: LILAC }}>
              —
            </span>
            <span
              style={{
                fontFamily: FONT,
                fontSize: "1.35vw",
                fontWeight: 700,
                color: LILAC,
              }}
            >
              Starts in
            </span>
            <span
              style={{
                fontFamily: FONT,
                fontSize: "2.4vw",
                fontWeight: 900,
                color: CREAM,
                fontVariantNumeric: "tabular-nums",
                textShadow: "0 0 16px rgba(192,132,252,0.5)",
              }}
            >
              {strip.countdownStr}
            </span>
          </div>
        </>
      );
    }

    // ===== STANDBY (purple) =====
    const isPureStandby =
      lockedRows.length === 0 &&
      nonLockedRows.length > 0 &&
      nonLockedRows.every((r) => r.type === "standby");
    if (isPureStandby) {
      const detail =
        nonLockedRows.find((r) => r.type === "standby")?.detail || "";
      const left2 =
        detail === "Tap Here 5 Min Before Showtime"
          ? "5 MINUTES BEFORE SHOWTIME!"
          : detail;
      return barShell(
        <>
          {glare}
          {/* LEFT — brand + message */}
          <div
            style={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            {brandLabel(CREAM)}
            <div
              style={{
                fontFamily: FONT,
                fontSize: "1.85vw",
                fontWeight: 800,
                lineHeight: 1.1,
                color: WHITE,
                marginTop: "0.4vh",
                whiteSpace: "nowrap",
                animation: "mbTextPulse 2.6s ease-in-out infinite",
              }}
            >
              {left2}
            </div>
          </div>
          {/* RIGHT — Up Next pill */}
          {strip.visible && strip.show && (
            <div
              style={{
                position: "relative",
                zIndex: 2,
                display: "flex",
                alignItems: "baseline",
                gap: "0.8vw",
                whiteSpace: "nowrap",
              }}
            >
              <span
                style={{
                  fontFamily: FONT,
                  fontSize: "1.35vw",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: PRE_WHITE,
                }}
              >
                {strip.prefix}
              </span>
              <span
                style={{
                  fontFamily: FONT,
                  fontSize: "1.9vw",
                  fontWeight: 800,
                  color: WHITE,
                }}
              >
                {strip.show}
              </span>
              {strip.timeStr && (
                <>
                  <span
                    style={{ fontFamily: FONT, fontSize: "1.5vw", color: LILAC }}
                  >
                    @
                  </span>
                  <span
                    style={{
                      fontFamily: FONT,
                      fontSize: "1.9vw",
                      fontWeight: 800,
                      color: CREAM,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {strip.timeStr}
                  </span>
                </>
              )}
            </div>
          )}
        </>
      );
    }

    // ===== WRAP (purple) — "That's a wrap! / See you tomorrow" =====
    if (isWrap) {
      return barShell(
        <>
          {glare}
          <div
            style={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
            }}
          >
            {brandLabel(CREAM)}
          </div>
          <div
            style={{
              position: "relative",
              zIndex: 2,
              display: "flex",
              alignItems: "baseline",
              gap: "0.8vw",
              whiteSpace: "nowrap",
            }}
          >
            <span
              style={{
                fontFamily: FONT,
                fontSize: "1.9vw",
                fontWeight: 800,
                color: PEND_SHOW,
              }}
            >
              {strip.prefix}
            </span>
            <span
              style={{ fontFamily: FONT, fontSize: "1.6vw", color: LILAC }}
            >
              {strip.show}
            </span>
          </div>
        </>
      );
    }

    // ===== GENERIC FALLBACK (purple) — remaining mbStatus / strip states =====
    // Real Display strings, centered. Covers upcoming-only, closed,
    // countdown-preview, and the Now Playing / Starting Now pill.
    const rows = [];
    const cap = {
      fontFamily: FONT,
      fontSize: "1.1vw",
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: LILAC,
    };
    const showS = {
      fontFamily: FONT,
      fontSize: "1.9vw",
      fontWeight: 800,
      color: WHITE,
    };
    const detailS = {
      fontFamily: FONT,
      fontSize: "1.6vw",
      fontWeight: 700,
      color: CREAM,
    };
    const timeS = {
      fontFamily: FONT,
      fontSize: "1.9vw",
      fontWeight: 800,
      color: CREAM,
      fontVariantNumeric: "tabular-nums",
    };
    const rowWrap = {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "center",
      gap: "0.8vw",
      whiteSpace: "nowrap",
    };

    nonLockedRows.forEach((row, i) => {
      if (row.type === "closed") {
        rows.push(
          <div key={`n${i}`} style={rowWrap}>
            <span style={detailS}>Opens at</span>
            <span style={timeS}>{row.opensAt}</span>
          </div>
        );
      } else if (row.type === "countdown-cta") {
        rows.push(
          <div key={`n${i}`} style={rowWrap}>
            <span style={showS}>{row.names}:</span>
            <span style={{ ...detailS, color: WHITE }}>
              {ctaText}
            </span>
          </div>
        );
      } else if (row.type === "countdown-preview") {
        rows.push(
          <div key={`n${i}`} style={rowWrap}>
            <span style={showS}>{row.showName}</span>
            <span style={detailS}>starts in</span>
            <span style={timeS}>
              {formatCountdown(Math.max(0, row.msUntilStart))}
            </span>
          </div>
        );
      } else if (row.type === "upcoming") {
        rows.push(
          <div key={`n${i}`} style={rowWrap}>
            <span style={{ ...cap, color: PRE_WHITE }}>Up Next:</span>
            <span style={showS}>{row.names}</span>
            <span style={{ fontFamily: FONT, fontSize: "1.5vw", color: LILAC }}>
              @
            </span>
            <span style={timeS}>{row.formattedTime}</span>
          </div>
        );
      }
    });

    if (rows.length === 0 && strip.visible && strip.show) {
      rows.push(
        <div key="strip" style={rowWrap}>
          <span style={{ ...cap, color: PRE_WHITE }}>{strip.prefix}</span>
          <span style={showS}>{strip.show}</span>
          {strip.timeStr && (
            <>
              <span
                style={{ fontFamily: FONT, fontSize: "1.5vw", color: LILAC }}
              >
                @
              </span>
              <span style={timeS}>{strip.timeStr}</span>
            </>
          )}
        </div>
      );
    }

    if (rows.length === 0) return null;

    return barShell(
      <>
        {glare}
        <div
          style={{
            position: "relative",
            zIndex: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.3vh",
            width: "100%",
          }}
        >
          {rows}
        </div>
      </>
    );
  };

  // Is the bar currently showing an actionable CTA the remote can fire?
  // True for LOCKED (connect) and WITHIN-5-MIN (early entry). The remote
  // "press down twice" gesture fires whichever applies.
  const beaconBarHasAction = () => {
    const { lockedRows = [], nonLockedRows = [] } = beacon.mbStatus || {};
    if (lockedRows.length > 0) return true;
    if (nonLockedRows.some((r) => r.type === "countdown-cta")) return true;
    return false;
  };

  const fireBeaconBarAction = () => {
    const { lockedRows = [], nonLockedRows = [] } = beacon.mbStatus || {};
    if (lockedRows.length > 0) {
      beacon.handleLockedRowClick(lockedRows[0]);
      return;
    }
    const ctaRow = nonLockedRows.find((r) => r.type === "countdown-cta");
    if (ctaRow) {
      beacon.handleCountdownCtaClick(ctaRow);
    }
  };

  return (
    <div
      style={{
        // WRAPPER — holds the night background, the optional status bar,
        // and the grid. When the bar is hidden this is a plain full-screen
        // column with a single 100vw×100vh grid child, so the grid is
        // byte-identical to the previous production layout. The background
        // moved here from the grid (visually identical for bar-off).
        width: "100vw",
        height: "100vh",
        overflow: "hidden",
        fontFamily: "Lato",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {showBeaconBar && (
        <style>{`
          @keyframes mbBarPulse {
            0%, 100% { box-shadow: 0 4px 18px rgba(34,197,94,0.28); }
            50% { box-shadow: 0 4px 30px rgba(34,197,94,0.55); }
          }
          @keyframes mbBarGlare {
            0% { left: -120%; }
            55% { left: 230%; }
            100% { left: 230%; }
          }
          @keyframes mbTextPulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.55; }
          }
        `}</style>
      )}
      {showBeaconBar && renderBeaconBar()}
      <div
        style={{
          // GRID — when the bar is shown, scale DOWN by the bar factor in
          // BOTH width and height so each cell keeps its exact aspect ratio
          // (no squashing); the wrapper centers it horizontally. When the
          // bar is hidden, width/height are exactly "100vw"/"100vh" — the
          // original values — so the layout matches current production.
          width: showBeaconBar ? `${100 - BEACON_BAR_VH}vw` : "100vw",
          height: showBeaconBar ? `${100 - BEACON_BAR_VH}vh` : "100vh",
          maxWidth: "100vw",
          maxHeight: "100vh",
          flex: "0 0 auto",
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          padding: "12px",
          flexWrap: "wrap",
          overflow: "hidden",
        }}
      >
      {Array(totalPlayers)
        .fill()
        .map((_, index) => {
          const id = videoIds[index] || "";
          const isSelected = selectedIndex === index;
          const isFullscreen = fullscreenIndex === index;
          const showEndedOverlay = endedIndexes.includes(index) || !id;
          const streamerName = id ? videoIdToStreamer[id] || "" : "";
          const isPreRecorded =
            streamerName && PRE_RECORDED_NAMES.has(streamerName);
          const isLive =
            id &&
            Object.entries(liveStreams || {}).some(
              ([name, data]) =>
                data?.videoId === id && !PRE_RECORDED_NAMES.has(name)
            );
          // Step 2a: per-slot Magic Beacon lock styling. Read the same
          // magic state Display uses. TV styles inline (no Beacon CSS
          // import) so the purple lock cues are self-contained here.
          const isMagicLocked =
            beacon.getMagicStateForVideoId(id, streamerName) === "proven";

          return (
            <button
              key={index}
              ref={(el) => (videoGridRefs.current[index] = el)}
              // onFocus={() => setSelectedIndex(index)}
              style={{
                height:
                  isFullscreen || totalPlayers === 1
                    ? "100vh"
                    : `calc(${100 / gridSize}% - 12px)`,
                width:
                  isFullscreen || totalPlayers === 1
                    ? "100vw"
                    : `calc(${100 / gridSize}% - 30px)`,
                backgroundColor: "black",
                position:
                  isFullscreen || totalPlayers === 1 ? "absolute" : "relative",
                top: isFullscreen || totalPlayers === 1 ? 0 : 0,
                left: isFullscreen || totalPlayers === 1 ? 0 : 0,
                right: isFullscreen || totalPlayers === 1 ? 0 : 0,
                bottom: isFullscreen || totalPlayers === 1 ? 0 : 0,
                zIndex: isFullscreen || totalPlayers === 1 ? 1000 : 1,
                outline:
                  isFullscreen || totalPlayers === 1
                    ? "6px solid transparent"
                    : isSelected && !beaconBarFocused
                    ? // Outline only on the focused slot. Its COLOR tells
                      // you whether the focused slot is locked (purple) or
                      // not (green). Cleared when the Beacon bar is
                      // focused so only one element reads as selected.
                      isMagicLocked
                      ? "6px solid #c084fc"
                      : "6px solid #05f651"
                    : "6px solid transparent",
                boxShadow:
                  isSelected &&
                  !beaconBarFocused &&
                  isMagicLocked &&
                  !(isFullscreen || totalPlayers === 1)
                    ? "0 0 24px rgba(192, 132, 252, 0.7)"
                    : "none",
                borderRadius: isFullscreen || totalPlayers === 1 ? 0 : "12px",
                overflow: "hidden",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
              onClick={() => {
                handleSelectIndex(index);
                // handleDoubleTap(index);
                setShowVideoThumbnails(true);
              }}
              tabIndex={!showVideoThumbnails && !showGridConfigModal ? 0 : -1}
            >
              <div className="click-capture" />
              {id && !beacon.syncMode.active ? (
                <YouTubePlayer
                  key={id || index}
                  ref={(r) => (playerRefs.current[index] = r)}
                  videoId={id}
                  isSelected={isSelected}
                  onEnded={() => handleOnVideoEnded(id)}
                  onError={() => handleOnVideoEnded(id)}
                />
              ) : (
                <div className="empty-player"></div>
              )}
              {streamerName && (
                <div
                  className="streamer-label"
                  style={{
                    fontSize: 19,
                  }}
                >
                  @{streamerName}
                </div>
              )}
              {isMagicLocked ? (
                <div
                  style={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    fontWeight: "bold",
                    padding: "4px 8px",
                    borderRadius: 4,
                    zIndex: 30,
                    boxShadow: "0 0 4px rgba(0, 0, 0, 0.4)",
                    letterSpacing: 1,
                    fontSize: 16,
                    backgroundColor: "#7c3aed",
                    color: "white",
                  }}
                >
                  MAGIC BEACON LIVE
                </div>
              ) : isPreRecorded ? (
                <div
                  className="pre-recorded-badge"
                  style={{
                    fontSize: 16,
                  }}
                >
                  PRE-RECORDED
                </div>
              ) : isLive ? (
                <div
                  className="live-badge"
                  style={{
                    fontSize: 16,
                  }}
                >
                  LIVE
                </div>
              ) : null}
              {showEndedOverlay && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "black",
                    color: "white",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "2.4rem",
                    padding: "20px",
                    textAlign: "center",
                    zIndex: 20,
                  }}
                >
                  {id ? (
                    <>
                      This LIVE has ended.
                      <br />
                      Select Another Stream To Continue...
                    </>
                  ) : (
                    <>
                      No Live Streams Currently Available
                      <br />
                      Check Back Soon!
                    </>
                  )}
                </div>
              )}

              {/* ===== In-fullscreen Magic Beacon mb-status badge =====
                  Renders when this slot is fullscreen (or is the only
                  slot in 1-screen mode) AND the hook has produced a
                  badge entry for it (preview/countdown/syncing/locked
                  phase). This is what shows the AUTOSYNC heads-up
                  ("Your bands will automatically pair to this stream!")
                  before the hook's auto-promote setTimeout fires at
                  countdown, and the locked CTA inside fullscreen.

                  Source of truth is the hook's fsBadges — identical to
                  Display (BeaconMultiViewDisplay.jsx line 642). On TV
                  the badge is sized up for couch viewing and ignores
                  the `tappable` click handler since remotes don't tap
                  badges; the user gets here via:
                    - the auto-promote (no tap needed), or
                    - the down-twice bar CTA, or
                    - the locked-fullscreen rule that already routes
                      directly into sync (so we never see `tappable`
                      in practice — but Display shows it visually). */}
              {(isFullscreen || totalPlayers === 1) &&
                beacon.fsBadges &&
                beacon.fsBadges[index] && (() => {
                  const b = beacon.fsBadges[index];
                  const isLocked = b.phase === "locked";
                  const bg = isLocked
                    ? "linear-gradient(135deg,#16a34a,#15803d)"
                    : b.phase === "syncing"
                    ? "linear-gradient(135deg,#7c3aed,#5b21b6)"
                    : "linear-gradient(135deg,#7c3aed,#4c1d95)";
                  // Shadow color matches phase identity — green for
                  // connected/locked, purple for everything else.
                  const shadow = isLocked
                    ? "0 0 28px rgba(34,197,94,0.55)"
                    : "0 0 24px rgba(124,58,237,0.45)";
                  return (
                    <div
                      style={{
                        position: "absolute",
                        top: "3vh",
                        right: "3vw",
                        zIndex: 50,
                        padding: "1.6vh 2vw",
                        borderRadius: "14px",
                        background: bg,
                        color: "#fff",
                        fontFamily: "Lato, sans-serif",
                        maxWidth: "38vw",
                        textAlign: "right",
                        boxShadow: shadow,
                        pointerEvents: "none",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "1.25vw",
                          fontWeight: 900,
                          letterSpacing: "0.5px",
                          color: "#fde68a",
                          marginBottom: "0.6vh",
                        }}
                      >
                        {b.label}
                      </div>
                      <div
                        style={{
                          fontSize: "1.5vw",
                          fontWeight: 800,
                          lineHeight: 1.1,
                          marginBottom: "0.4vh",
                        }}
                      >
                        {b.detail}
                        {typeof b.remaining === "number" && b.remaining > 0 && (
                          <>
                            {" "}
                            <span style={{ fontFamily: "monospace" }}>
                              {(() => {
                                const totalSec = Math.max(
                                  0,
                                  Math.floor(b.remaining / 1000)
                                );
                                const m = Math.floor(totalSec / 60);
                                const s = totalSec % 60;
                                return `${m}:${s.toString().padStart(2, "0")}`;
                              })()}
                            </span>
                          </>
                        )}
                      </div>
                      {(b.showName || b.showTimeStr) && (
                        <div
                          style={{
                            fontSize: "1.45vw",
                            fontWeight: 700,
                            color: "#e9d5ff",
                          }}
                        >
                          {b.showName}
                          {b.showName && b.showTimeStr ? " @ " : ""}
                          {b.showTimeStr}
                        </div>
                      )}
                    </div>
                  );
                })()}
            </button>
          );
        })}

      {/* Thumbnails */}
      <div
        id="thumbnail-scroll"
        className="thumbnail-scroll"
        style={{
          position: "absolute",
          bottom: "0",
          left: "0",
          right: "0",
          zIndex: 1000,
          transition: "all 0.3s",
          opacity: showVideoThumbnails ? 1 : 0,
          backgroundColor: "rgba(59, 60, 60, 0.8)",
        }}
      >
        {[
          { videoId: "vip_exit" },
          { videoId: "close" },
          { videoId: "settings" },
          { videoId: "force_live" },
          { videoId: "beacon_toggle" },
          
          ...allThumbnails,
        ].map((stream, index) => {
          const disabled = currentVideoIds.includes(stream.videoId);
          const isThumbnailSelected = selectedThumbnailIndex === index;
          // Step 2a: is this streamer's thumbnail locked? Control items
          // (vip_exit/close/settings) have no videoId/streamer so this is
          // false for them. Used only in the streamer-tile branch below.
          const thumbMagic =
            stream.videoId &&
            !["vip_exit", "close", "settings", "beacon_toggle", "force_live"].includes(
              stream.videoId
            )
              ? beacon.getMagicStateForVideoId(
                  stream.videoId,
                  stream.streamer
                ) === "proven"
              : false;

          if (stream.videoId === "vip_exit") {
            return (
              <button
                key={stream.videoId}
                ref={(el) => (thumbnailRefs.current[index] = el)}
                className={`thumb-box ${disabled ? "disabled" : ""} ${
                  isThumbnailSelected ? "selected" : ""
                }`}
                onClick={() => {
                  updateView("VIP", () => {
                    setShowBottomBar(true);
                  });
                }}
                onFocus={() => handleThumbnailFocus(index)}
                style={{
                  outline: isThumbnailSelected
                    ? "6px solid #05f651"
                    : "6px solid white",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "white",
                  background: "transparent",
                  backgroundColor: "red",
                  padding: "8px 12px",
                  border: "none",
                  cursor: "pointer",
                }}
                tabIndex={showVideoThumbnails ? 0 : -1}
              >
                <span
                  style={{
                    fontSize: 34,
                    textAlign: "center",
                    fontWeight: "bold",
                    color: "white",
                  }}
                >
                  VIP
                  <br /> EXIT
                </span>
              </button>
            );
          }

          if (stream.videoId === "close") {
            return (
              <button
                key={stream.videoId}
                ref={(el) => (thumbnailRefs.current[index] = el)}
                className={`thumb-box ${disabled ? "disabled" : ""} ${
                  isThumbnailSelected ? "selected" : ""
                }`}
                onClick={() => {
                  console.log("close");
                  setShowVideoThumbnails(false);
                }}
                onFocus={() => handleThumbnailFocus(index)}
                style={{
                  outline: isThumbnailSelected
                    ? "6px solid #05f651"
                    : "6px solid white",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "white",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
                tabIndex={showVideoThumbnails ? 0 : -1}
              >
                <IoClose size={80} color="white" />
              </button>
            );
          }
          if (stream.videoId === "settings") {
            return (
              <button
                key={stream.videoId}
                ref={(el) => (thumbnailRefs.current[index] = el)}
                className={`thumb-box ${disabled ? "disabled" : ""} ${
                  isThumbnailSelected ? "selected" : ""
                }`}
                onClick={() => {
                  setShowVideoThumbnails(false);
                  setSelectedGridOption(0);
                }}
                onFocus={() => handleThumbnailFocus(index)}
                style={{
                  outline: isThumbnailSelected
                    ? "6px solid #05f651"
                    : "6px solid white",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "white",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
                tabIndex={showVideoThumbnails ? 0 : -1}
              >
                <MdGridView size={80} color="white" />
              </button>
            );
          }

          if (stream.videoId === "beacon_toggle") {
            // BEACON INFO toggle. ON = vibrant purple gradient with a
            // soft glow + subtle inner border, matching the visual
            // language of the in-fullscreen badge and the bar itself.
            // OFF = clean transparent so it doesn't dominate the bar
            // when disabled. Label changed from "STATUS BAR" to
            // "BEACON INFO" — clearer about what it actually toggles.
            return (
              <button
                key={stream.videoId}
                ref={(el) => (thumbnailRefs.current[index] = el)}
                className={`thumb-box ${isThumbnailSelected ? "selected" : ""}`}
                onClick={() => toggleBeaconBar()}
                onFocus={() => handleThumbnailFocus(index)}
                style={{
                  outline: isThumbnailSelected
                    ? "6px solid #05f651"
                    : "6px solid white",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "white",
                  background: showBeaconBar
                    ? "linear-gradient(135deg, #7c3aed 0%, #5b21b6 55%, #4c1d95 100%)"
                    : "transparent",
                  boxShadow: showBeaconBar
                    ? "0 0 18px rgba(124, 58, 237, 0.55), inset 0 0 0 1px rgba(255, 255, 255, 0.18)"
                    : "none",
                  padding: "8px 12px",
                  border: "none",
                  cursor: "pointer",
                  minWidth: 130,
                  fontFamily: "Lato, sans-serif",
                }}
                tabIndex={showVideoThumbnails ? 0 : -1}
              >
                <span
                  style={{
                    fontSize: 30,
                    fontWeight: 900,
                    letterSpacing: 2,
                    color: showBeaconBar ? "#fde68a" : "#fff",
                    lineHeight: 1,
                    textShadow: showBeaconBar
                      ? "0 1px 2px rgba(0,0,0,0.45)"
                      : "none",
                  }}
                >
                  BEACON
                </span>
                <span
                  style={{
                    fontSize: 30,
                    fontWeight: 900,
                    letterSpacing: 2,
                    color: showBeaconBar ? "#fde68a" : "#fff",
                    lineHeight: 1,
                    marginTop: 4,
                    marginBottom: 8,
                    textShadow: showBeaconBar
                      ? "0 1px 2px rgba(0,0,0,0.45)"
                      : "none",
                  }}
                >
                  INFO
                </span>
                <span
                  style={{
                    fontSize: 28,
                    fontWeight: 900,
                    letterSpacing: 2,
                    color: "#fff",
                    lineHeight: 1,
                    textShadow: showBeaconBar
                      ? "0 2px 6px rgba(0,0,0,0.55)"
                      : "none",
                  }}
                >
                  {showBeaconBar ? "ON" : "OFF"}
                </span>
              </button>
            );
          }

          if (stream.videoId === "force_live") {
            return (
              <button
                key={stream.videoId}
                ref={(el) => (thumbnailRefs.current[index] = el)}
                className={`thumb-box ${isThumbnailSelected ? "selected" : ""}`}
                onClick={() => {
                  forceLiveAtIndex(selectedIndex);
                  setShowVideoThumbnails(false);
                }}
                onFocus={() => handleThumbnailFocus(index)}
                style={{
                  outline: isThumbnailSelected
                    ? "6px solid #05f651"
                    : "6px solid white",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  color: "white",
                  background: "transparent",
                  padding: "8px 12px",
                  border: "none",
                  cursor: "pointer",
                  minWidth: 130,
                  fontFamily: "Lato, sans-serif",
                }}
                tabIndex={showVideoThumbnails ? 0 : -1}
              >
                <style>{`
                  @keyframes flBlink {
                    0%, 100% { opacity: 1; box-shadow: 0 0 14px rgba(239,68,68,0.9); }
                    50% { opacity: 0.25; box-shadow: 0 0 2px rgba(239,68,68,0.2); }
                  }
                `}</style>
                <span
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    backgroundColor: "#ef4444",
                    marginBottom: 10,
                    animation: "flBlink 1.1s ease-in-out infinite",
                  }}
                />
                <span
                  style={{
                    fontSize: 30,
                    fontWeight: 900,
                    letterSpacing: 2,
                    lineHeight: 1,
                  }}
                >
                  FORCE
                </span>
                <span
                  style={{
                    fontSize: 30,
                    fontWeight: 900,
                    letterSpacing: 2,
                    lineHeight: 1,
                    marginTop: 4,
                  }}
                >
                  LIVE
                </span>
              </button>
            );
          }


          return (
            <button
              key={stream.videoId}
              ref={(el) => (thumbnailRefs.current[index] = el)}
              className={`thumb-box ${disabled ? "disabled" : ""} ${
                isThumbnailSelected ? "selected" : ""
              }`}
              onClick={() => !disabled && handleVideoSelect(stream.videoId)}
              onFocus={() => handleThumbnailFocus(index)}
              disabled={disabled}
              style={{
                // Step 2a: outline only on the focused thumb. Color is
                // purple if that streamer is locked, green otherwise.
                // Unfocused thumbs show no outline (lock reads via the
                // SYNC NOW badge below).
                outline: isThumbnailSelected
                  ? thumbMagic
                    ? "6px solid #c084fc"
                    : "6px solid #05f651"
                  : "6px solid transparent",
                boxShadow:
                  isThumbnailSelected && thumbMagic
                    ? "0 0 18px rgba(192, 132, 252, 0.7)"
                    : "none",
                background: "transparent",
                border: "none",
                cursor: disabled ? "not-allowed" : "pointer",
                opacity: disabled ? 0.6 : 1,
              }}
              tabIndex={showVideoThumbnails ? 0 : -1}
            >
              <img src={stream.thumbnail} alt={stream.title} />
              <div className="thumb-meta">
                <strong
                  style={{
                    maxWidth: "180px",
                    width: "100%",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: 18,
                  }}
                >
                  @{stream.streamer}
                </strong>
                <div className="badge-row">
                  {thumbMagic ? (
                    <div
                      style={{
                        position: "static",
                        fontSize: 16,
                        fontWeight: "bold",
                        padding: "4px 8px",
                        borderRadius: 4,
                        letterSpacing: 1,
                        backgroundColor: "#7c3aed",
                        color: "white",
                      }}
                    >
                      SYNC NOW
                    </div>
                  ) : stream.isLoop ? (
                    <div
                      className="pre-recorded-badge"
                      style={{ position: "static", fontSize: 16 }}
                    >
                      PRE-RECORDED
                    </div>
                  ) : (
                    <div
                      className="live-badge"
                      style={{ position: "static", fontSize: 14 }}
                    >
                      WATCH LIVE!
                    </div>
                  )}
                </div>
                {disabled && (
                  <div
                    className="playing-label"
                    style={{
                      fontSize: 12,
                    }}
                  >
                    (Currently Playing)
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {showGridConfigModal && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            background: "rgba(255, 255, 255, 0.35)", // soft translucent white
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 20,
            flexDirection: "column",
          }}
        >
          <h2
            style={{
              color: "#ffffff",
              fontSize: "44px",
              marginBottom: "24px",
              fontWeight: 600,
              letterSpacing: "0.5px",
              textAlign: "center",
              textShadow: "0 3px 6px rgba(0, 0, 0, 0.75)",
              background:
                "linear-gradient(90deg, rgba(0,0,0,0.6), rgba(0,0,0,0.2), rgba(0,0,0,0.6))",
              padding: "10px 24px",
              borderRadius: "12px",
            }}
          >
            Select Your Layout
          </h2>

          <div style={{ display: "flex", gap: 20 }}>
            <button
              style={{
                background:
                  selectedGridOption === 0
                    ? "linear-gradient(180deg, #003cff, #00034c)"
                    : "linear-gradient(180deg, #001a66, #00034c)",
                border:
                  selectedGridOption === 0
                    ? "6px solid #05f651"
                    : "6px solid #1a6eff",
                borderRadius: "20px",
                padding: "20px 30px",
                width: "200px",
                height: "160px",
                color: "white",
                fontSize: "24px",
                fontWeight: "bold",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 20px rgba(0, 255, 255, 0.3)",
                textShadow: "0 2px 4px rgba(0,0,0,0.6)",
                transform: selectedGridOption === 0 ? "scale(1.1)" : "scale(1)",
                transition: "all 0.2s ease-in-out",
                gap: "12px",
              }}
            >
              {/* Fixed-height icon wrapper for vertical alignment */}
              <div
                style={{
                  height: "60px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* 2x2 grid icon */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gridTemplateRows: "1fr 1fr",
                    gap: "6px",
                    width: "50px",
                    height: "50px",
                  }}
                >
                  {Array(4)
                    .fill(0)
                    .map((_, i) => (
                      <div
                        key={i}
                        style={{
                          backgroundColor: "white",
                          borderRadius: "4px",
                        }}
                      />
                    ))}
                </div>
              </div>
              4 Streams
            </button>

            <button
              style={{
                background:
                  selectedGridOption === 1
                    ? "linear-gradient(180deg, #003cff, #00034c)"
                    : "linear-gradient(180deg, #001a66, #00034c)",
                border:
                  selectedGridOption === 1
                    ? "6px solid #05f651"
                    : "6px solid #1a6eff",
                borderRadius: "20px",
                padding: "20px 30px",
                width: "200px",
                height: "160px",
                color: "white",
                fontSize: "24px",
                fontWeight: "bold",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 20px rgba(0, 255, 255, 0.3)",
                textShadow: "0 2px 4px rgba(0,0,0,0.6)",
                transform: selectedGridOption === 1 ? "scale(1.1)" : "scale(1)",
                transition: "all 0.2s ease-in-out",
                gap: "12px",
              }}
            >
              {/* Fixed height wrapper for icon alignment */}
              <div
                style={{
                  height: "60px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  gap: "6px",
                }}
              >
                {/* Top row: 2 boxes side by side */}
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      backgroundColor: "white",
                      borderRadius: "4px",
                    }}
                  />
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      backgroundColor: "white",
                      borderRadius: "4px",
                    }}
                  />
                </div>
                {/* Bottom row: 1 centered box */}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      backgroundColor: "white",
                      borderRadius: "4px",
                    }}
                  />
                </div>
              </div>
              3 Streams
            </button>

            <button
              style={{
                background:
                  selectedGridOption === 2
                    ? "linear-gradient(180deg, #003cff, #00034c)"
                    : "linear-gradient(180deg, #001a66, #00034c)",
                border:
                  selectedGridOption === 2
                    ? "6px solid #05f651"
                    : "6px solid #1a6eff",
                borderRadius: "20px",
                padding: "20px 30px",
                width: "200px",
                height: "160px",
                color: "white",
                fontSize: "24px",
                fontWeight: "bold",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 20px rgba(0, 255, 255, 0.3)",
                textShadow: "0 2px 4px rgba(0,0,0,0.6)",
                transform: selectedGridOption === 2 ? "scale(1.1)" : "scale(1)",
                transition: "all 0.2s ease-in-out",
                gap: "12px",
              }}
            >
              {/* Fixed height wrapper for alignment */}
              <div
                style={{
                  height: "60px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Icon: 2 side-by-side blocks */}
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                  }}
                >
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      backgroundColor: "white",
                      borderRadius: "4px",
                    }}
                  />
                  <div
                    style={{
                      width: "22px",
                      height: "22px",
                      backgroundColor: "white",
                      borderRadius: "4px",
                    }}
                  />
                </div>
              </div>
              2 Streams
            </button>

            <button
              style={{
                background:
                  selectedGridOption === 3
                    ? "linear-gradient(180deg, #003cff, #00034c)"
                    : "linear-gradient(180deg, #001a66, #00034c)",
                border:
                  selectedGridOption === 3
                    ? "6px solid #05f651"
                    : "6px solid #1a6eff",
                borderRadius: "20px",
                padding: "20px 30px",
                width: "200px",
                height: "160px",
                color: "white",
                fontSize: "24px",
                fontWeight: "bold",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 20px rgba(0, 255, 255, 0.3)",
                textShadow: "0 2px 4px rgba(0,0,0,0.6)",
                transform: selectedGridOption === 3 ? "scale(1.1)" : "scale(1)",
                transition: "all 0.2s ease-in-out",
                gap: "12px",
              }}
            >
              {/* Fixed-height icon wrapper for alignment */}
              <div
                style={{
                  height: "60px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {/* Single centered square */}
                <div
                  style={{
                    width: "40px",
                    height: "40px",
                    backgroundColor: "white",
                    borderRadius: "4px",
                  }}
                />
              </div>
              1 Stream
            </button>

            <button
              style={{
                background:
                  selectedGridOption === 4
                    ? "linear-gradient(180deg, #ff4c4c, #800000)"
                    : "linear-gradient(180deg, #a80000, #4d0000)",
                border:
                  selectedGridOption === 4
                    ? "6px solid #05f651"
                    : "6px solid #ff4c4c",
                borderRadius: "20px",
                padding: "20px 30px",
                width: "200px",
                height: "160px",
                color: "white",
                fontSize: "24px",
                fontWeight: "bold",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 20px rgba(255, 0, 0, 0.3)",
                textShadow: "0 2px 4px rgba(0,0,0,0.6)",
                transform: selectedGridOption === 4 ? "scale(1.1)" : "scale(1)",
                transition: "all 0.2s ease-in-out",
                gap: "12px",
              }}
            >
              {/* Icon wrapper with centered “X” */}
              <div
                style={{
                  height: "60px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "52px",
                    fontWeight: 900,
                    lineHeight: "1",
                    color: "white",
                  }}
                >
                  ×
                </div>
              </div>
              Close
            </button>
          </div>
        </div>
      )}
      </div>

      {/* ===== SyncFullscreen — full-screen HLS doors + sync experience.
          1:1 with Display: once mounted it owns the whole screen, no TV
          chrome needed. isTv=true → the hook/player target the 1080p HLS
          ladder. ===== */}
      {beacon.syncMode.active && (
        <SyncFullscreen
          key={beacon.syncMode.streamId}
          streamId={beacon.syncMode.streamId}
          isTv={true}
          streamerName={beacon.syncStreamerName}
          deviceId={beaconDeviceId}
          sendBeaconCmd={beacon.sendBeaconCmd}
          sendCancel={beacon.sendCancel}
          anchor={beacon.syncMode.anchor}
          cues={beacon.syncCues}
          graceModeActive={beacon.graceModeActive}
          onShowPosTick={beacon.onShowPosTick}
          earlyEntry={beacon.syncMode.earlyEntry}
          earlyEntryShowId={beacon.syncMode.earlyEntryShowId}
          showtimeMs={beacon.syncMode.showtimeMs}
          hlsVolume={beacon.hlsVolume}
          hlsMuted={beacon.hlsMuted}
          tinkUserEnabled={beacon.tinkUserEnabled}
          findLockedPeer={beacon.findLockedPeer}
          onPeerHandoff={beacon.peerHandoff}
          isController={beacon.isController}
          hasHolder={beacon.hasHolder}
          holder={beacon.holder}
          controlSynced={beacon.controlSynced}
          controlKeyReady={beacon.controlKeyReady}
          claimControl={beacon.claimControl}
          onExit={() => {
            // The 4-grid restoration is now handled by the
            // syncMode.active true → false effect at the top of this
            // component, which catches ALL exit paths (user EXIT,
            // show-end, mid-show unlock, beacon disabled). Just trigger
            // the deactivation here; the effect will do the rest.
            beacon.exitSyncMode("user closed");
          }}
        />
      )}

      {/* ===== Stream picker overlay (TV) — appears when the bar CTA fires
          and there are MULTIPLE eligible/locked streamers to choose from.
          Remote-navigable grid of streamer thumbnails. A full-size CLOSE
          tile is the FIRST item so the user can back out. Selecting a tile
          enters the HLS sync (doors → fullscreen). Wording/behavior mirror
          Display's stream-picker; styling is inline + TV-sized. ===== */}
      {beacon.streamPicker.open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 4000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(6,6,16,0.92)",
            fontFamily: "Lato, sans-serif",
          }}
        >
          <h2
            style={{
              color: "#fde68a",
              fontSize: "2.4vw",
              fontWeight: 900,
              letterSpacing: "0.5px",
              margin: "0 0 3vh 0",
              textAlign: "center",
              textShadow: "0 2px 6px rgba(0,0,0,0.6)",
            }}
          >
            {beacon.streamPicker.title}
          </h2>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1.6vw",
              justifyContent: "center",
              alignItems: "center",
              maxWidth: "86vw",
            }}
          >
            {/* CLOSE tile — first, same size as a thumbnail */}
            <button
              ref={(el) => (pickerRefs.current[0] = el)}
              onClick={() => beacon.closeStreamPicker()}
              onFocus={() => setPickerIndex(0)}
              tabIndex={0}
              style={{
                width: "20vw",
                aspectRatio: "16 / 9",
                borderRadius: "12px",
                background: "#dc2626",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#fff",
                outline:
                  pickerIndex === 0
                    ? "6px solid #05f651"
                    : "6px solid rgba(255,255,255,0.25)",
                border: "none",
              }}
            >
              <IoClose size={70} color="#fff" />
              <span
                style={{
                  fontSize: "1.3vw",
                  fontWeight: 800,
                  marginTop: "0.6vh",
                }}
              >
                CLOSE
              </span>
            </button>

            {beacon.streamPicker.items.map((item, i) => {
              const idx = i + 1; // +1 because CLOSE is index 0
              const isLockedContext =
                beacon.streamPicker.mode === "locked" ||
                beacon.streamPicker.mode === "locked-multi";
              const earlyEntry =
                beacon.streamPicker.mode === "early" ||
                beacon.streamPicker.mode === "early-multi";
              const tileShowId =
                item.pickerShowId ||
                (earlyEntry
                  ? beacon.streamPicker.showId
                  : beacon.getAnyAnchor(item.streamId)?.showId || null);
              const showLabel = tileShowId ? prettyShow(tileShowId) : "";
              const ytId = item.meta?.youtubeUrl
                ? extractYouTubeId(item.meta.youtubeUrl)
                : null;
              const thumb = ytId
                ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`
                : 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 9"><rect width="16" height="9" fill="%23222"/></svg>';
              return (
                <button
                  key={item.streamId}
                  ref={(el) => (pickerRefs.current[idx] = el)}
                  onClick={() => beacon.handlePickerTileClick(item)}
                  onFocus={() => setPickerIndex(idx)}
                  tabIndex={0}
                  style={{
                    width: "20vw",
                    aspectRatio: "16 / 9",
                    borderRadius: "12px",
                    overflow: "hidden",
                    position: "relative",
                    background: "#000",
                    cursor: "pointer",
                    padding: 0,
                    border: "none",
                    outline:
                      pickerIndex === idx
                        ? isLockedContext
                          ? "6px solid #4ade80"
                          : "6px solid #c084fc"
                        : "6px solid transparent",
                    boxShadow:
                      pickerIndex === idx
                        ? "0 0 24px rgba(192,132,252,0.6)"
                        : "none",
                  }}
                >
                  {showLabel && (
                    <div
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        padding: "0.5vh 0.6vw",
                        fontSize: "1.05vw",
                        fontWeight: 800,
                        color: "#fff",
                        background: isLockedContext
                          ? "rgba(34,80,40,0.85)"
                          : "rgba(40,20,70,0.85)",
                        textAlign: "left",
                      }}
                    >
                      {showLabel}
                    </div>
                  )}
                  <img
                    src={thumb}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      padding: "0.6vh 0.6vw",
                      fontSize: "1.25vw",
                      fontWeight: 800,
                      color: "#fff",
                      background:
                        "linear-gradient(transparent, rgba(0,0,0,0.8))",
                      textAlign: "left",
                    }}
                  >
                    @{item.meta?.name || item.streamId}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== Peer-handoff overlay (TV) — replaces Display's gold
          tap-bubble. Pops when, mid-sync-wait, another streamer is already
          locked on the same show. Same sounds play inside SyncFullscreen;
          this is the remote-friendly YES/NO chooser. ===== */}
      {peerOffer && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 4500,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(6,6,16,0.9)",
            fontFamily: "Lato, sans-serif",
          }}
        >
          {/* Gold peer-handoff card — yellow background signals
              "another streamer is offering you their sync." Sized to
              match the in-fullscreen badge family (vw units). */}
          <div
            style={{
              background:
                "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              padding: "5vh 5vw",
              borderRadius: "20px",
              border: "3px solid #fde68a",
              boxShadow:
                "0 0 50px rgba(245,158,11,0.6), 0 20px 60px rgba(0,0,0,0.7)",
              maxWidth: "70vw",
              textAlign: "center",
            }}
          >
            <div
              style={{
                fontSize: "2vw",
                fontWeight: 900,
                color: "#1f0a00",
                textAlign: "center",
                marginBottom: "1.4vh",
                letterSpacing: "1px",
                textShadow: "0 1px 0 rgba(255,255,255,0.25)",
              }}
            >
              Another Streamer Has Already Synced
            </div>
            <div
              style={{
                fontSize: "1.4vw",
                fontWeight: 700,
                color: "#fff",
                textAlign: "center",
                marginBottom: "4vh",
                textShadow: "0 1px 2px rgba(0,0,0,0.45)",
              }}
            >
              Press OK to watch sooner!
            </div>
            <div style={{ display: "flex", gap: "2vw", justifyContent: "center" }}>
              <button
                ref={(el) => (peerRefs.current[0] = el)}
                onClick={() => {
                  beacon.peerHandoff(peerOffer.streamId);
                  setPeerOffer(null);
                }}
                onFocus={() => setPeerIndex(0)}
                tabIndex={0}
                style={{
                  fontFamily: "Lato, sans-serif",
                  fontSize: "1.6vw",
                  fontWeight: 900,
                  // Blue YES button with white letters — same change as
                  // the SfTvPeerOverlay (HLS-side). Keeps the lime-green
                  // focus outline visually distinct from the button.
                  color: "#fff",
                  background: "#2563eb",
                  border: "none",
                  borderRadius: "12px",
                  padding: "1.6vh 3vw",
                  cursor: "pointer",
                  outline:
                    peerIndex === 0
                      ? "6px solid #05f651"
                      : "6px solid transparent",
                }}
              >
                YES, SWITCH
              </button>
              <button
                ref={(el) => (peerRefs.current[1] = el)}
                onClick={() => setPeerOffer(null)}
                onFocus={() => setPeerIndex(1)}
                tabIndex={0}
                style={{
                  fontFamily: "Lato, sans-serif",
                  fontSize: "1.6vw",
                  fontWeight: 900,
                  color: "#fff",
                  background: "#dc2626",
                  border: "none",
                  borderRadius: "12px",
                  padding: "1.6vh 3vw",
                  cursor: "pointer",
                  outline:
                    peerIndex === 1
                      ? "6px solid #05f651"
                      : "6px solid transparent",
                }}
              >
                NO, STAY
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BeaconMultiViewTV;
